import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import AppTheme from '../theme/AppTheme';

const TEST_ACCOUNTS = [
  { name: 'Cole Beasley', email: 'cole@demo.com', password: 'password123' },
  { name: 'Jane Cooper', email: 'jane@demo.com', password: 'password123' },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = React.useState(true);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [name, setName] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  // Forgot / reset password
  const [forgotOpen, setForgotOpen] = React.useState(false);
  const [forgotEmail, setForgotEmail] = React.useState('');
  const [forgotNewPw, setForgotNewPw] = React.useState('');
  const [forgotConfirmPw, setForgotConfirmPw] = React.useState('');
  const [forgotError, setForgotError] = React.useState('');
  const [forgotSuccess, setForgotSuccess] = React.useState(false);
  const [forgotLoading, setForgotLoading] = React.useState(false);

  const openForgot = () => {
    setForgotEmail(email); // pre-fill if they already typed their email
    setForgotNewPw(''); setForgotConfirmPw('');
    setForgotError(''); setForgotSuccess(false);
    setForgotOpen(true);
  };

  const handleResetPassword = async () => {
    setForgotError('');
    if (!forgotEmail) { setForgotError('Email is required'); return; }
    if (forgotNewPw !== forgotConfirmPw) { setForgotError('Passwords do not match'); return; }
    if (forgotNewPw.length < 6) { setForgotError('Password must be at least 6 characters'); return; }
    setForgotLoading(true);
    try {
      await api.resetPassword(forgotEmail, forgotNewPw);
      setForgotSuccess(true);
    } catch (err: any) {
      setForgotError(err.message || 'Failed to reset password');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        if (!name.trim()) {
          setError('Name is required');
          setLoading(false);
          return;
        }
        await register(email, password, name);
      }
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickLogin = async (account: typeof TEST_ACCOUNTS[0]) => {
    setError('');
    setLoading(true);
    try {
      await login(account.email, account.password);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Stack spacing={2} sx={{ maxWidth: 400, width: '100%', mx: 2 }}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h4" component="h1" gutterBottom textAlign="center">
                {isLogin ? 'Welcome Back' : 'Create Account'}
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
                {isLogin ? 'Sign in to your account' : 'Register for a new account'}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  {!isLogin && (
                    <TextField
                      label="Name"
                      type="text"
                      fullWidth
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      disabled={loading}
                    />
                  )}
                  <TextField
                    label="Email"
                    type="email"
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                  />
                  <TextField
                    label="Password"
                    type="password"
                    fullWidth
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    helperText={!isLogin && 'At least 6 characters'}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading}
                    sx={{ mt: 2 }}
                  >
                    {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Register'}
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError('');
                  }}
                  disabled={loading}
                  sx={{ textTransform: 'none' }}
                >
                  {isLogin ? "Don't have an account? Register" : 'Already have an account? Sign In'}
                </Button>
                {isLogin && (
                  <>
                    <br />
                    <Button
                      size="small"
                      onClick={openForgot}
                      disabled={loading}
                      sx={{ textTransform: 'none', color: 'text.secondary', fontSize: 13 }}
                    >
                      Forgot password?
                    </Button>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Test accounts panel */}
          {isLogin && (
            <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Divider sx={{ mb: 2 }}>
                  <Chip label="Demo Accounts" size="small" />
                </Divider>
                <Stack spacing={1}>
                  {TEST_ACCOUNTS.map((account) => (
                    <Button
                      key={account.email}
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled={loading}
                      onClick={() => handleQuickLogin(account)}
                      sx={{
                        justifyContent: 'space-between',
                        textTransform: 'none',
                        px: 2,
                        py: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {account.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {account.email}
                      </Typography>
                    </Button>
                  ))}
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  textAlign="center"
                  sx={{ mt: 1.5 }}
                >
                  Password for all: <strong>password123</strong>
                </Typography>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Box>

      {/* Reset Password Dialog */}
      <Dialog open={forgotOpen} onClose={() => setForgotOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {forgotSuccess ? (
              <Alert severity="success">
                Password reset successfully. You can now sign in with your new password.
              </Alert>
            ) : (
              <>
                {forgotError && <Alert severity="error">{forgotError}</Alert>}
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  disabled={forgotLoading}
                  autoComplete="email"
                />
                <TextField
                  label="New Password"
                  type="password"
                  fullWidth
                  value={forgotNewPw}
                  onChange={(e) => setForgotNewPw(e.target.value)}
                  disabled={forgotLoading}
                  helperText="At least 6 characters"
                  autoComplete="new-password"
                />
                <TextField
                  label="Confirm New Password"
                  type="password"
                  fullWidth
                  value={forgotConfirmPw}
                  onChange={(e) => setForgotConfirmPw(e.target.value)}
                  disabled={forgotLoading}
                  autoComplete="new-password"
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForgotOpen(false)} disabled={forgotLoading}>
            {forgotSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!forgotSuccess && (
            <Button
              variant="contained"
              onClick={handleResetPassword}
              disabled={forgotLoading || !forgotEmail || !forgotNewPw || !forgotConfirmPw}
            >
              {forgotLoading ? 'Resetting…' : 'Reset Password'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </AppTheme>
  );
}
